/**
 * Ad create / edit — writes through Surface B (`management.ads`). The form morphs by creative type. Creative
 * assets are entered as URLs here (a drag-and-drop uploader lands with the resource endpoints, M6). The type
 * is immutable on edit. POPUNDER budgets/urls are derived server-side.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { management, hierarchy, ApiError, type Ad, type AdCreative } from '../lib/api';
import { Creative } from '../components/Creative';
import { Text, Select, Check } from './CampaignForm';

type Form = Record<string, unknown>;
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const num = (v: unknown): number | null => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null; };
/** A creative asset URL that is actually a video file — some DISPLAY creatives are MP4 banners. */
const isVideoUrl = (u: string | null): boolean => !!u && /\.(mp4|webm|mov|m4v|ogv|ogg)(\?|#|$)/i.test(u);

const NO_PREVIEW = new Set(['POPUNDER', 'ADSIMILATE_EXTERNAL_LINK']);

/**
 * The creative to preview — built LIVE from the form so it updates as you edit the URLs, falling back to
 * the ad's stored creative (which follows the legacy rules: a rendered `snapshot` beats the raw asset).
 */
function livePreview(t: string, f: Form, stored: Ad | null): AdCreative | null {
  if (NO_PREVIEW.has(t)) return null;
  if (t === 'TEXT') return { kind: 'text', imageUrl: null, videoUrl: null, audioUrl: null, width: null, height: null };
  if (t === 'AUDIO') {
    return {
      kind: 'audio',
      audioUrl: str(f.audio_url) || stored?.creative?.audioUrl || null,
      imageUrl: str(f.image_url) || stored?.creative?.imageUrl || null, videoUrl: null,
      width: num(f.creative_width) ?? stored?.creative?.width ?? null,
      height: num(f.creative_height) ?? stored?.creative?.height ?? null,
    };
  }
  if (['VIDEO', 'VIDEO_IN_BANNER'].includes(t) || (t === 'DOOH' && f.dooh_content_type === 'video')) {
    // The edited video URL plays; the still comes from the ad's stored thumbnail/snapshot.
    return {
      kind: 'video',
      videoUrl: str(f.video_url) || stored?.creative?.videoUrl || null,
      imageUrl: stored?.creative?.imageUrl ?? null,
      audioUrl: null, width: null, height: null,
    };
  }
  // Image types (DISPLAY, DOOH image): the edited URL wins so the preview tracks what you type. But the
  // asset itself may be a video (an MP4 banner) with a still snapshot — detect that and render the player
  // plus the still instead of trying to load an MP4 as an <img> (which is the "asset did not load" case).
  const asset = str(f.image_url) || stored?.creative?.videoUrl || stored?.creative?.imageUrl || null;
  if (isVideoUrl(asset)) {
    const still = stored?.creative?.imageUrl && !isVideoUrl(stored.creative.imageUrl) ? stored.creative.imageUrl : null;
    return { kind: 'video', videoUrl: asset, imageUrl: still, audioUrl: null, width: null, height: null };
  }
  return asset
    ? { kind: 'image', imageUrl: asset, videoUrl: null, audioUrl: null, width: num(f.creative_width) ?? stored?.creative?.width ?? null, height: num(f.creative_height) ?? stored?.creative?.height ?? null }
    : (stored?.creative ?? { kind: 'none', imageUrl: null, videoUrl: null, audioUrl: null, width: null, height: null });
}

// NATIVE, SOCIAL and RICH_MEDIA are no longer supported creative types (the bidder cannot serve them).
const TYPES = [
  'DISPLAY', 'HTML5', 'JAVASCRIPT', 'TEXT', 'POPUNDER', 'AUDIO', 'VIDEO', 'VIDEO_IN_BANNER',
  'DOOH', 'ADSIMILATE_EXTERNAL_LINK',
];

export function AdFormScreen(): JSX.Element {
  const { id, adgroupId: adgroupIdParam } = useParams<{ id: string; adgroupId: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [adgroupId, setAdgroupId] = useState(adgroupIdParam ?? '');
  const [campaignId, setCampaignId] = useState('');
  const [f, setF] = useState<Form>({
    type: 'DISPLAY', title: '', description: '', description_2: '', original_url: '', display_url: '',
    is_active: true, image_url: '', creative_width: '', creative_height: '', video_url: '', audio_url: '',
    index_html_url: '', js_ad_tag: '', dooh_content_type: 'image', video_autoplay: false,
    audio_title: '', audio_duration: '30', gender: 'ALL', language: 'ALL', audio_stations: '', audio_podcasts: '',
    external_campaign_id: '', max_daily_click: '0', limit_total_amount: '15', limit_daily_amount: '5',
  });
  const [loading, setLoading] = useState(editing);
  const [loadFailed, setLoadFailed] = useState(false);
  const [storedAd, setStoredAd] = useState<Ad | null>(null);
  // The ad's ORIGINAL nested `ad` object, kept so a save merges edits into it rather than replacing it —
  // otherwise editing (say) the title would drop the creative (`creative_url`/`snapshot_url`), which the
  // management update REPLACES wholesale. `creativeField` is which key actually holds the image URL.
  const [origAd, setOrigAd] = useState<Form>({});
  const [creativeField, setCreativeField] = useState<'image_url' | 'creative_url'>('image_url');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }));
  // Return to the ad group when we know it; otherwise fall back to browser-back so Cancel always works
  // (on an edit whose ad failed to load, adgroupId is still empty).
  const goBack = useCallback(() => { if (adgroupId) navigate(`/adgroups/${adgroupId}`); else navigate(-1); }, [adgroupId, navigate]);

  useEffect(() => {
    if (!editing || !id) return;
    let live = true;
    management.ads.meta(id).then(({ ad: a, meta }) => {
      if (!live) return;
      setAdgroupId(str(a.adgroup_id)); setCampaignId(str((meta as Form).campaign_id));
      const ad = (a.ad ?? {}) as Form;
      setOrigAd(ad);
      // Prod ads store the creative under `creative_url` (+ `snapshot_url`), not always `image_url` — so the
      // field mirrors whichever holds it, and edits are written back to that same key.
      setCreativeField(ad.image_url ? 'image_url' : (ad.creative_url ? 'creative_url' : 'image_url'));
      // Dimensions live in creative_width/height OR snapshot_width/height — one pair is 0/absent, the other
      // holds the real size (this ad reads 0 on creative_* but 300×250 on snapshot_*). Show whichever is real.
      const cw = num(ad.creative_width) ?? num(ad.snapshot_width);
      const ch = num(ad.creative_height) ?? num(ad.snapshot_height);
      setF((p) => ({
        ...p, type: str(a.type) || 'DISPLAY', title: str(a.title), description: str(a.description),
        description_2: str(a.description_2), original_url: str(a.original_url), display_url: str(a.display_url),
        is_active: a.is_active === 'Y', image_url: str(ad.image_url) || str(ad.creative_url),
        creative_width: cw != null ? String(cw) : '', creative_height: ch != null ? String(ch) : '',
        video_url: str(ad.video_url), audio_url: str(ad.audio_url),
        index_html_url: str(ad.html5_index_html_url), js_ad_tag: str(a.js_ad_tag),
        dooh_content_type: str(a.dooh_content_type) || 'image', audio_title: str(a.audio_title),
        audio_duration: str(a.audio_duration) || '30', external_campaign_id: str(a.external_campaign_id),
      }));
      setLoading(false);
    }).catch((e) => { setError(e instanceof ApiError ? e.message : 'Could not load the ad.'); setLoadFailed(true); setLoading(false); });
    return () => { live = false; };
  }, [editing, id]);

  // The ad's NORMALISED creative (snapshot etc.) for the preview. Non-fatal — a broken preview must not
  // block editing, so a failure here is swallowed and the live-from-fields preview still renders.
  useEffect(() => {
    if (!editing || !id) return;
    let live = true;
    hierarchy.ad(id).then((a) => { if (live) setStoredAd(a); }).catch(() => {});
    return () => { live = false; };
  }, [editing, id]);

  const buildBody = (): Form => {
    const t = str(f.type);
    // Start from the ad's EXISTING nested object so unrelated fields (creative_url, snapshot_url, the
    // POPUNDER/audio internals) survive a save — the management update REPLACES `ad`, so a fresh {} here
    // would wipe them. On create, origAd is {} so this is a no-op.
    const ad: Form = { ...origAd };
    if (['DISPLAY', 'DOOH'].includes(t)) {
      if (f.image_url) ad[creativeField] = f.image_url;   // write the image back to the key the ad uses
      ad.creative_width = f.creative_width; ad.creative_height = f.creative_height;
    }
    if (['VIDEO', 'VIDEO_IN_BANNER', 'DOOH'].includes(t) && f.video_url) ad.video_url = f.video_url;
    if (t === 'AUDIO' && f.audio_url) ad.audio_url = f.audio_url;
    if (t === 'HTML5' && f.index_html_url) ad.html5_index_html_url = f.index_html_url;
    const body: Form = {
      type: t, adgroup_id: adgroupId, campaign_id: campaignId, title: f.title, description: f.description,
      description_2: f.description_2, original_url: f.original_url, display_url: f.display_url,
      is_active: f.is_active ? 'Y' : 'N', ad,
    };
    if (t === 'JAVASCRIPT') body.js_ad_tag = f.js_ad_tag;
    if (t === 'DOOH') body.dooh_content_type = f.dooh_content_type;
    if (t === 'VIDEO_IN_BANNER') body.video_autoplay = f.video_autoplay ? 'Y' : 'N';
    if (t === 'AUDIO') { body.audio_title = f.audio_title; body.audio_duration = f.audio_duration; body.audio_stations = f.audio_stations; body.audio_podcasts = f.audio_podcasts; body.audience = { gender: f.gender, language: f.language }; }
    if (['POPUNDER', 'ADSIMILATE_EXTERNAL_LINK'].includes(t)) { body.limit_total_amount = f.limit_total_amount; body.limit_daily_amount = f.limit_daily_amount; body.max_daily_click = f.max_daily_click; }
    if (t === 'ADSIMILATE_EXTERNAL_LINK') body.external_campaign_id = f.external_campaign_id;
    return body;
  };

  const submit = useCallback(async () => {
    setError(null);
    setSaving(true);
    try {
      if (editing && id) await management.ads.update(id, buildBody());
      else await management.ads.create(buildBody());
      goBack();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Save failed.'); setSaving(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, adgroupId, campaignId, editing, id, goBack, origAd, creativeField]);

  if (loading) return <div className="busy">Loading…</div>;
  if (loadFailed) return (
    <div className="mform">
      <h1>Edit ad</h1>
      <div className="banner error">{error ?? 'Could not load this ad.'}</div>
      <div className="mform-actions"><button className="button" onClick={goBack}>Back</button></div>
    </div>
  );
  const t = str(f.type);
  const showImage = ['DISPLAY', 'DOOH'].includes(t);
  const showVideo = ['VIDEO', 'VIDEO_IN_BANNER', 'DOOH'].includes(t);

  // A synthetic ad for the preview: identity + copy from the form, creative built live (falls back to stored).
  const previewAd: Ad = {
    id: id ?? '', campaignId, adGroupId: adgroupId, type: t,
    title: str(f.title), description: str(f.description),
    destinationUrl: storedAd?.destinationUrl ?? '', displayUrl: str(f.display_url),
    originalUrl: str(f.original_url), isActive: Boolean(f.is_active),
    creative: livePreview(t, f, storedAd),
  };

  return (
    <div className="mform">
      <h1>{editing ? 'Edit ad' : 'New ad'}</h1>
      <p className="sub">The creative type shapes the fields. Assets are URLs for now — a drag-and-drop uploader is coming.</p>
      {error && <div className="banner error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <section>
        <h2>Creative</h2>
        {!NO_PREVIEW.has(t) && (
          <div className="creative-preview">
            <span className="creative-preview-label">{editing ? 'Current creative' : 'Preview'}</span>
            <Creative ad={previewAd} />
          </div>
        )}
        <div className="mgrid">
          <div className="field">
            <label htmlFor="type">Type</label>
            <select id="type" value={t} disabled={editing} onChange={(e) => set('type', e.target.value)}>
              {TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            {editing && <span className="hint">Type cannot change after creation.</span>}
          </div>
          <Check f={f} set={set} k="is_active" label="Active" />
          {t !== 'POPUNDER' && <Text f={f} set={set} k="title" label="Title" full />}
          {t === 'TEXT' && <Text f={f} set={set} k="description" label="Description" full />}
          {t === 'TEXT' && <Text f={f} set={set} k="description_2" label="Description line 2" full />}
          {t !== 'POPUNDER' && t !== 'ADSIMILATE_EXTERNAL_LINK' && <Text f={f} set={set} k="original_url" label="Click / landing URL" full />}
          {['TEXT', 'JAVASCRIPT'].includes(t) && <Text f={f} set={set} k="display_url" label="Display URL" />}

          {showImage && <><Text f={f} set={set} k="image_url" label="Image URL" full /><Text f={f} set={set} k="creative_width" label="Width" type="number" /><Text f={f} set={set} k="creative_height" label="Height" type="number" /></>}
          {showVideo && <Text f={f} set={set} k="video_url" label="Video URL" full />}
          {t === 'HTML5' && <Text f={f} set={set} k="index_html_url" label="HTML5 index URL" full />}
          {t === 'JAVASCRIPT' && <div className="field full"><label htmlFor="js">JS ad tag</label><textarea id="js" value={str(f.js_ad_tag)} onChange={(e) => set('js_ad_tag', e.target.value)} /></div>}
          {t === 'DOOH' && <Select f={f} set={set} k="dooh_content_type" label="DOOH content" options={['image', 'video']} />}
          {t === 'VIDEO_IN_BANNER' && <Check f={f} set={set} k="video_autoplay" label="Autoplay" />}
        </div>
      </section>

      {t === 'AUDIO' && (
        <section>
          <h2>Audio</h2>
          <div className="mgrid">
            <Text f={f} set={set} k="audio_title" label="Audio title" />
            <Select f={f} set={set} k="audio_duration" label="Duration (s)" options={['15', '30', '45', '60']} />
            <Text f={f} set={set} k="audio_url" label="Audio URL" full />
            <Select f={f} set={set} k="gender" label="Gender" options={['ALL', 'MALE', 'FEMALE']} />
            <Select f={f} set={set} k="language" label="Language" options={['ALL', 'EN', 'ES']} />
            <Text f={f} set={set} k="audio_stations" label="Stations" hint="Comma-separated" />
            <Text f={f} set={set} k="audio_podcasts" label="Podcasts" hint="Comma-separated" />
          </div>
        </section>
      )}

      {['POPUNDER', 'ADSIMILATE_EXTERNAL_LINK'].includes(t) && (
        <section>
          <h2>Pacing</h2>
          <div className="mgrid">
            <Text f={f} set={set} k="limit_total_amount" label="Total budget" type="number" />
            <Text f={f} set={set} k="limit_daily_amount" label="Daily budget" type="number" />
            <Text f={f} set={set} k="max_daily_click" label="Max daily clicks" type="number" />
            {t === 'ADSIMILATE_EXTERNAL_LINK' && <Text f={f} set={set} k="external_campaign_id" label="External campaign ID" full />}
          </div>
        </section>
      )}

      <div className="mform-actions">
        <button className="button primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create ad'}</button>
        <button className="button" onClick={goBack}>Cancel</button>
      </div>
    </div>
  );
}
